import React from "react";
import { Navigate, Outlet, matchPath, useLocation } from "react-router-dom";
import { getUserFromToken } from "../utils/apiUtils";

/**
 * Organization Admin Protected Route Component
 * Checks if user is authenticated and has BitNetOrgAdmin role
 * CRITICAL: BitNetOrgAdmin can ONLY access the paths listed in allowedPaths below.
 * All other routes redirect to /clients. Any new top-level page/route added to
 * App.js MUST also be added here, or it will silently redirect back to
 * /clients with no error shown to the user (see 2026-09-23 incident: the
 * Renewals & Collections page was fully built, routed, and deployed, but
 * unreachable for the entire BitNetOrgAdmin user base because this allowlist
 * was never updated).
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components to render if authorized
 * @returns {React.Component} Children or redirect to login/clients
 */
const OrgAdminProtectedRoute = () => {
  const token = localStorage.getItem("token");
  const location = useLocation();

  // Allowed paths for BitNetOrgAdmin
  const allowedPaths = [
    "/", "/clients", "/clients/new", "/clients/:clientId", "/clients/:clientId/:section",
    "/tickets", "/tickets/:ticketNumber", "/monitoring", "/client-monitoring", "/renewals",
  ];

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Decode user from token
  const user = getUserFromToken();

  if (!user) {
    localStorage.removeItem("token");
    return <Navigate to="/login" replace />;
  }

  // Check if user has BitNetOrgAdmin role
  if (user.role !== "BitNetOrgAdmin") {
    console.warn("Access denied: User does not have BitNetOrgAdmin role");
    return <Navigate to="/login" replace />;
  }

  // CRITICAL: BitNetOrgAdmin can ONLY access allowed paths
  if (!allowedPaths.some((path) => matchPath({ path, end: true }, location.pathname))) {
    console.warn(`BitNetOrgAdmin attempting to access unauthorized route: ${location.pathname}`);
    return <Navigate to="/clients" replace />;
  }

  return <Outlet />;
};

export default OrgAdminProtectedRoute;
